// Slide 5 — Forces in a Turn.  CINEMATIC HYBRID test-then-reveal.
//
// During narration (progress-driven):
//   - Sky gradient + drifting cloud layer
//   - Tilting horizon line — banks OPPOSITE the aircraft, giving the
//     in-cockpit "we're banking" feel
//   - Cessna 172-style side-view silhouette
//   - Aircraft tilts smoothly to 30° angle of bank
//   - LIFT vector tilts with the aircraft (perpendicular to wing chord)
//   - WEIGHT always points straight down (world frame)
//
// Held back until quiz answered:
//   - slideQuizFlags[0] → vertical + horizontal lift components,
//     centripetal/centrifugal/load-factor labels
//   - slideQuizFlags[1] → "If LIFT increases → DRAG increases →
//     AIRSPEED decreases" conclusion bar
import React from "react";

const PHASE = {
  LEVEL: [0.00, 0.18],
  BANK:  [0.18, 0.45],
};

function window01(progress, [s, e]) {
  if (progress <= s) return 0;
  if (progress >= e) return 1;
  return (progress - s) / (e - s);
}
function reached(progress, [s]) { return progress >= s; }

export default function Slide05ForcesInTurn({ progress, slideQuizFlags = [] }) {
  const bankProg = window01(progress, PHASE.BANK);
  const bankAngle = bankProg * 30;

  const showComponents = !!slideQuizFlags[0];
  const showConclusion = !!slideQuizFlags[1];

  const levelCaptionOpacity = progress < 0.20 ? 1 : Math.max(0, 1 - (progress - 0.20) * 6);
  const bankCaptionOpacity = reached(progress, PHASE.BANK) ? 1 : 0;
  const showPrompt = reached(progress, [0.45]) && !showComponents;

  return (
    <div className="w-full h-full relative overflow-hidden">
      {/* Sky gradient */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, #0b1c33 0%, #1b3151 60%, #213a5e 100%)"
      }} />

      {/* Drifting clouds */}
      <CloudsLayer />

      {/* Header */}
      <div className="absolute top-6 left-8 right-8 flex items-baseline justify-between z-10">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/50 font-semibold">
          Aerodynamics
        </div>
        <div className="text-2xl sm:text-3xl font-heading font-bold text-white tracking-tight">
          Forces in a Turn
        </div>
      </div>

      <div
        className="absolute top-24 left-8 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFA300] z-10"
        style={{ opacity: levelCaptionOpacity, transition: "opacity 400ms" }}
      >
        LEVEL FLIGHT · LIFT = WEIGHT
      </div>
      <div
        className="absolute top-24 left-8 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFA300] z-10"
        style={{ opacity: bankCaptionOpacity, transition: "opacity 400ms" }}
      >
        30° ANGLE OF BANK · LEVEL TURN
      </div>

      {showPrompt && (
        <div className="absolute top-24 right-8 px-4 py-2 rounded-sm border border-[#FFA300]/40 bg-[#FFA300]/10 backdrop-blur-sm fade-up z-10">
          <span className="text-[#FFA300] text-xs uppercase tracking-[0.16em] font-bold">
            Does lift still equal weight?
          </span>
        </div>
      )}

      <svg
        viewBox="0 0 800 500"
        className="w-[92%] h-[78%] mt-14 mx-auto block relative z-[5]"
        style={{ overflow: "visible" }}
      >
        {/* Horizon — tilts OPPOSITE to bank so the cockpit-view illusion works.
            The plane stays upright relative to viewer; horizon rolls instead. */}
        <g transform={`translate(400 370) rotate(${-bankAngle})`}>
          <line
            x1="-500" y1="0" x2="500" y2="0"
            stroke="#3d5878" strokeWidth="1.5" strokeDasharray="4 6"
          />
          {/* Ground hint shading below the horizon */}
          <rect
            x="-700" y="0" width="1400" height="220"
            fill="url(#groundGrad)"
            opacity="0.18"
          />
        </g>
        <defs>
          <linearGradient id="groundGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#3d5878" />
            <stop offset="100%" stopColor="#0b1c33" />
          </linearGradient>
        </defs>

        {/* Aircraft (rotates around its centre) + LIFT vector glued to it */}
        <g
          transform={`translate(400 250) rotate(${bankAngle})`}
          style={{ transition: "transform 50ms linear" }}
        >
          {/* Cessna 172 side-view silhouette */}
          <CessnaSideView />

          {/* LIFT vector — perpendicular to wings (so it rotates with the bank) */}
          <Vector
            from={[0, -6]} to={[0, -170]} color="#FFA300"
            label="LIFT" labelOffset={[18, 4]} show={true} strokeWidth={6}
          />
        </g>

        {/* WEIGHT — always straight down (world frame) */}
        <Vector
          from={[400, 256]} to={[400, 420]} color="#9ab1cc"
          label="WEIGHT" labelOffset={[20, 0]} show={true} strokeWidth={4}
        />

        {/* COMPONENTS — held back until quiz 1 answered */}
        <g style={{ opacity: showComponents ? 1 : 0, transition: "opacity 600ms" }}>
          <Vector
            from={[400, 244]} to={[400, 100]} color="#7BD389"
            label="VERTICAL" sublabel="component of lift"
            labelOffset={[-180, -10]} show={true} strokeWidth={4} dashed
          />
          <Vector
            from={[400, 244]} to={[560, 244]} color="#5AB6FF"
            label="HORIZONTAL" sublabel="component of lift"
            labelOffset={[10, 26]} show={true} strokeWidth={4} dashed
          />
          {/* Construction lines from lift tip to component tips */}
          <line
            x1={400 + Math.sin((bankAngle * Math.PI) / 180) * 164}
            y1={250 - Math.cos((bankAngle * Math.PI) / 180) * 170}
            x2="400" y2="100"
            stroke="#FFA300" strokeOpacity="0.35" strokeDasharray="2 4" strokeWidth="1.5"
          />
          <line
            x1={400 + Math.sin((bankAngle * Math.PI) / 180) * 164}
            y1={250 - Math.cos((bankAngle * Math.PI) / 180) * 170}
            x2="560" y2="244"
            stroke="#FFA300" strokeOpacity="0.35" strokeDasharray="2 4" strokeWidth="1.5"
          />
          <text x="580" y="226" fill="#5AB6FF" fontSize="13" fontWeight="700" fontFamily="Chivo, sans-serif">
            Centripetal force
          </text>
          <text x="580" y="244" fill="#9ab1cc" fontSize="11" fontFamily="Chivo, sans-serif">
            (toward turn centre)
          </text>
          <text x="270" y="436" fill="#FFA300" fontSize="13" fontWeight="700" fontFamily="Chivo, sans-serif">
            Apparent weight / Load factor
          </text>
        </g>
      </svg>

      {/* Conclusion bar — held back until quiz 2 answered */}
      <div
        className="absolute bottom-5 left-8 right-8 px-5 py-3 rounded-sm border border-[#FFA300]/40 bg-[#FFA300]/10 backdrop-blur-sm text-center z-10"
        style={{
          opacity: showConclusion ? 1 : 0,
          transform: `translateY(${showConclusion ? 0 : 12}px)`,
          transition: "opacity 500ms, transform 500ms",
        }}
      >
        <span className="text-white text-sm sm:text-base font-semibold tracking-wide">
          If <span className="text-[#FFA300]">LIFT</span> increases →
          {" "}<span className="text-[#FFA300]">DRAG</span> increases →
          {" "}<span className="text-[#FFA300]">AIRSPEED</span> decreases
        </span>
      </div>

      <style>{`
        @keyframes clouddrift5 { from { transform: translateX(40px); } to { transform: translateX(-40px); } }
        .s5cloud-far  { animation: clouddrift5 22s linear infinite alternate; }
        .s5cloud-near { animation: clouddrift5 14s linear infinite alternate; }
      `}</style>
    </div>
  );
}

/* --- Cessna 172 side-view silhouette, drawn around origin (0,0) at wing root --- */
function CessnaSideView() {
  return (
    <g>
      {/* Fuselage — flowing path */}
      <path
        d="
          M -110 -2
          L -80 -8
          L -25 -16
          L 35 -10
          L 60 -2
          L 60 14
          L 30 18
          L -75 18
          L -110 10
          Z
        "
        fill="#cdd9e8"
        stroke="#9ab1cc"
        strokeWidth="0.5"
      />
      {/* Cockpit window */}
      <path d="M 0 -16 L 15 -14 L 28 -8 L 28 -2 L 0 -2 Z" fill="#7d97b5" />
      {/* Wing (high-wing C172 — shown as a thin rectangle on top of fuselage) */}
      <rect x="-30" y="-22" width="60" height="6" rx="2" fill="#9ab1cc" />
      {/* Wing strut */}
      <line x1="-28" y1="-16" x2="-50" y2="14" stroke="#7d97b5" strokeWidth="1.5" />
      {/* Vertical tail */}
      <path d="M -110 -2 L -120 -36 L -100 -36 L -85 -2 Z" fill="#9ab1cc" />
      {/* Horizontal stabiliser */}
      <rect x="-115" y="-4" width="30" height="6" rx="1" fill="#cdd9e8" />
      {/* Propeller hub */}
      <circle cx="64" cy="6" r="3" fill="#52688b" />
      {/* Propeller disc (rotation blur) */}
      <ellipse cx="66" cy="6" rx="2" ry="22" fill="#52688b" opacity="0.55" />
      {/* Landing gear */}
      <line x1="-10" y1="18" x2="-18" y2="34" stroke="#7d97b5" strokeWidth="2.5" />
      <line x1=" 12" y1="18" x2=" 22" y2="34" stroke="#7d97b5" strokeWidth="2.5" />
      <ellipse cx="-18" cy="36" rx="5" ry="2.4" fill="#52688b" />
      <ellipse cx=" 22" cy="36" rx="5" ry="2.4" fill="#52688b" />
    </g>
  );
}

/* --- Drifting decorative clouds for slide 5 --- */
function CloudsLayer() {
  return (
    <svg
      viewBox="0 0 900 600"
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="xMidYMid slice"
    >
      <g className="s5cloud-far" opacity="0.10">
        <Cloud cx={140} cy={140} r={50} />
        <Cloud cx={720} cy={100} r={62} />
        <Cloud cx={460} cy={170} r={42} />
      </g>
      <g className="s5cloud-near" opacity="0.14">
        <Cloud cx={230} cy={430} r={70} />
        <Cloud cx={680} cy={470} r={84} />
        <Cloud cx={540} cy={530} r={52} />
      </g>
    </svg>
  );
}
function Cloud({ cx, cy, r }) {
  return (
    <g transform={`translate(${cx} ${cy})`} fill="#ffffff">
      <ellipse cx="-20" cy="0"   rx={r * 0.7}  ry={r * 0.45} />
      <ellipse cx=" 12" cy="-8"  rx={r * 0.55} ry={r * 0.5} />
      <ellipse cx=" 30" cy=" 4"  rx={r * 0.6}  ry={r * 0.4} />
    </g>
  );
}

function Vector({ from, to, color, label, sublabel, labelOffset = [10, 0], show, strokeWidth = 4, dashed = false }) {
  if (!show) return null;
  const [x1, y1] = from;
  const [x2, y2] = to;
  const [dx, dy] = labelOffset;
  const markerId = `vec5-${label.replace(/\s/g, "")}`;
  return (
    <g>
      <defs>
        <marker
          id={markerId} viewBox="0 0 10 10" refX="8" refY="5"
          markerWidth="6" markerHeight="6" orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
        </marker>
      </defs>
      <line
        x1={x1} y1={y1} x2={x2} y2={y2} stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round"
        strokeDasharray={dashed ? "6 5" : undefined}
        markerEnd={`url(#${markerId})`}
      />
      <text x={x2 + dx} y={y2 + dy} fill={color} fontSize="15" fontWeight="700" fontFamily="Chivo, sans-serif">
        {label}
      </text>
      {sublabel && (
        <text x={x2 + dx} y={y2 + dy + 16} fill="#9ab1cc" fontSize="11" fontFamily="Chivo, sans-serif">
          {sublabel}
        </text>
      )}
    </g>
  );
}
